/**
 * Canonical entity index — cross-investigation entity store.
 *
 * Canonical records are pointers, not replacements. Each investigation
 * keeps its own entity records with their own provenance and NATO ratings.
 * The canonical record says "these extracted entities across investigations
 * A, B, C refer to the same real-world thing."
 */

import { getDatabase } from '@/storage/database';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CanonicalEntity {
  canonicalId: string;
  name: string;
  type: string;
  firstSeenAt: string;
  lastSeenAt: string;
  investigationCount: number;
}

export interface CanonicalIdentifier {
  canonicalId: string;
  identifierType: string;
  identifierValue: string;
}

export interface CanonicalMatch {
  canonicalId: string;
  matchType: 'identifier' | 'name_exact' | 'name_fuzzy';
  confidence: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function db() {
  const conn = getDatabase();
  if (!conn) throw new Error('Database not initialized');
  return conn;
}

interface CanonicalEntityRow {
  canonical_id: string;
  name: string;
  type: string;
  first_seen_at: string;
  last_seen_at: string;
  investigation_count: number;
}

function rowToCanonical(row: CanonicalEntityRow): CanonicalEntity {
  return {
    canonicalId: row.canonical_id,
    name: row.name,
    type: row.type,
    firstSeenAt: row.first_seen_at,
    lastSeenAt: row.last_seen_at,
    investigationCount: row.investigation_count,
  };
}

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

/** Find canonical entity by strong identifier (MMSI, IMO, ICAO24, etc.) */
export async function findCanonicalByIdentifier(
  identifierType: string,
  identifierValue: string,
): Promise<CanonicalEntity | null> {
  const rows = await db().query<CanonicalEntityRow>(
    `SELECT ce.canonical_id, ce.name, ce.type, ce.first_seen_at, ce.last_seen_at, ce.investigation_count
     FROM canonical_entities ce
     JOIN canonical_identifiers ci ON ce.canonical_id = ci.canonical_id
     WHERE ci.identifier_type = $1 AND ci.identifier_value = $2`,
    [identifierType, identifierValue],
  );
  return rows.length > 0 ? rowToCanonical(rows[0]) : null;
}

/** Find canonical entity by exact normalized name + type match. */
export async function findCanonicalByName(
  name: string,
  type: string,
): Promise<CanonicalEntity | null> {
  const normalized = name.toLowerCase().trim();
  const rows = await db().query<CanonicalEntityRow>(
    `SELECT * FROM canonical_entities WHERE name = $1 AND type = $2`,
    [normalized, type],
  );
  return rows.length > 0 ? rowToCanonical(rows[0]) : null;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Create a new canonical entity record. Name is normalized (lowercase, trimmed). */
export async function createCanonicalEntity(
  entity: Omit<CanonicalEntity, 'investigationCount'>,
): Promise<void> {
  await db().execute(
    `INSERT INTO canonical_entities (canonical_id, name, type, first_seen_at, last_seen_at, investigation_count)
     VALUES ($1, $2, $3, $4, $5, 1)`,
    [entity.canonicalId, entity.name.toLowerCase().trim(), entity.type, entity.firstSeenAt, entity.lastSeenAt],
  );
}

/** Add a strong identifier to a canonical entity. */
export async function addCanonicalIdentifier(id: CanonicalIdentifier): Promise<void> {
  await db().execute(
    `INSERT OR IGNORE INTO canonical_identifiers (canonical_id, identifier_type, identifier_value)
     VALUES ($1, $2, $3)`,
    [id.canonicalId, id.identifierType, id.identifierValue],
  );
}

/** Link an extracted entity to a canonical entity. Updates investigation_count. */
export async function linkEntityToCanonical(
  entityId: string,
  canonicalId: string,
  investigationId: string,
  confidence: number,
  matchType: string,
): Promise<void> {
  await db().execute(
    `INSERT OR IGNORE INTO entity_canonical_map (entity_id, canonical_id, investigation_id, confidence, match_type, linked_at)
     VALUES ($1, $2, $3, $4, $5, $6)`,
    [entityId, canonicalId, investigationId, confidence, matchType, new Date().toISOString()],
  );
  // Update last_seen_at and recompute investigation_count
  await db().execute(
    `UPDATE canonical_entities SET last_seen_at = $1,
     investigation_count = (SELECT COUNT(DISTINCT investigation_id) FROM entity_canonical_map WHERE canonical_id = $2)
     WHERE canonical_id = $2`,
    [new Date().toISOString(), canonicalId],
  );
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Get all investigation IDs that reference a canonical entity. */
export async function getInvestigationsForCanonical(canonicalId: string): Promise<string[]> {
  const rows = await db().query<{ investigation_id: string }>(
    `SELECT DISTINCT investigation_id FROM entity_canonical_map WHERE canonical_id = $1`,
    [canonicalId],
  );
  return rows.map(r => r.investigation_id);
}

/** Get all canonical entities matching a given type (for cross-investigation browsing). */
export async function getCanonicalEntitiesByType(type: string): Promise<CanonicalEntity[]> {
  const rows = await db().query<CanonicalEntityRow>(
    `SELECT * FROM canonical_entities WHERE type = $1 ORDER BY investigation_count DESC`,
    [type],
  );
  return rows.map(rowToCanonical);
}
